import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './WheelPicker.css';

export interface WheelPickerProps {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  /** Ennyi lépésenként kap számfeliratot és magasabb vonalat a jelölő (alap: minden 5.). */
  majorEvery?: number;
  formatLabel?: (value: number) => string;
  unitLabel?: string;
  'aria-label': string;
}

const TICK_WIDTH = 12;
/** Egérgörgő: ennyi pixelnyi görgetés = egy lépés (egy rovátka ~100 px → 4 lépés). */
const WHEEL_PX_PER_STEP = 25;

/** A beírt szöveg értékké: vessző is jó, a határok közé és a lépésközre igazítva. */
export function snapTypedValue(text: string, min: number, max: number, step: number): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  const clamped = Math.min(max, Math.max(min, parsed));
  const index = Math.round((clamped - min) / step);
  return Math.round(Math.min(max, min + index * step) * 100) / 100;
}

/**
 * Vízszintesen görgethető „tárcsa" szám-választó — Geri kérése (GRUNDO #65):
 * a testsúly/magasság megadása legyen látványosabb és kezelhetőbb, mint egy
 * sima beviteli mező.
 *
 * CSS scroll-snap adja az illesztést, a görgetés VÉGÉT (nem minden
 * pöccintést) figyeljük — így nem hívjuk az `onChange`-et minden pixelnyi
 * mozgásra, csak amikor a felhasználó megállt egy értéken.
 *
 * Weben (Geri, #98): a sima egérgörgő is állítja (a sávon az oldal nem
 * görög), bal egérgombbal húzható, és a számra kattintva/koppintva be is
 * írható — korábban csak Shift+görgővel ment, és valakinél úgy sem.
 */
export function WheelPicker({
  min,
  max,
  step,
  value,
  onChange,
  majorEvery = 5,
  formatLabel,
  unitLabel,
  'aria-label': ariaLabel,
}: WheelPickerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const settleTimer = useRef<number | undefined>(undefined);
  const suppressUntil = useRef(0);
  const liveFrame = useRef<number | undefined>(undefined);
  const drag = useRef<{ pointerId: number; startX: number; startLeft: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const count = Math.round((max - min) / step) + 1;
  const clampedValue = Math.min(max, Math.max(min, value));

  /**
   * A MEGJELENÍTETT SZÁM görgetés KÖZBEN is kövesse az ujjat — Geri kérése
   * (#65): korábban csak a görgetés VÉGÉN (a settle-időzítő lejártakor)
   * frissült, ami úgy nézett ki, mintha a szám „ragadna". Ez a state
   * KÜLÖN a `value` prop-tól: minden scroll-eseményre azonnal frissül
   * (rAF-fal fojtva), a tényleges `onChange` commit viszont továbbra is
   * csak a görgetés VÉGÉN fut — így a szülő állapota nem pörög minden
   * pixelnyi mozgásra.
   */
  const [liveValue, setLiveValue] = useState(clampedValue);
  useEffect(() => {
    setLiveValue(clampedValue);
  }, [clampedValue]);

  const label = formatLabel ? formatLabel(liveValue) : String(liveValue);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Külső érték-változáskor (pl. mértékegység-váltás) a görgetőt is oda kell
  // állítani — enélkül a szám és a tárcsa pozíciója szétválna.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || containerWidth === 0) return;
    const index = Math.round((clampedValue - min) / step);
    const target = index * TICK_WIDTH;
    if (Math.abs(el.scrollLeft - target) < 1) return;
    suppressUntil.current = Date.now() + 300;
    el.scrollTo({ left: target, behavior: 'auto' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedValue, min, step, containerWidth]);

  // Egérgörgő a sávon: függőleges görgetés is lépteti. Natív, nem passzív
  // figyelő kell, különben a `preventDefault` nem fogja meg az oldalgörgetést.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let pending = 0;
    const onWheel = (event: WheelEvent) => {
      const raw = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (raw === 0) return;
      event.preventDefault();
      const px = event.deltaMode === 1 ? raw * 16 : event.deltaMode === 2 ? raw * el.clientWidth : raw;
      pending += px;
      const steps = Math.trunc(pending / WHEEL_PX_PER_STEP);
      if (steps === 0) return;
      pending -= steps * WHEEL_PX_PER_STEP;
      const current = Math.round(el.scrollLeft / TICK_WIDTH);
      const next = Math.max(0, Math.min(count - 1, current + steps));
      // Pontosan egy rovátkára: a scroll-snap nem rántja vissza.
      el.scrollLeft = next * TICK_WIDTH;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [count]);

  // Húzás bal egérgombbal. Érintésnél a natív görgetés marad.
  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    event.preventDefault();
    el.focus();
    el.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, startX: event.clientX, startLeft: el.scrollLeft };
    setDragging(true);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const current = drag.current;
    const el = scrollRef.current;
    if (!current || !el || event.pointerId !== current.pointerId) return;
    el.scrollLeft = current.startLeft - (event.clientX - current.startX);
  }

  function handlePointerEnd(event: React.PointerEvent<HTMLDivElement>) {
    const current = drag.current;
    const el = scrollRef.current;
    if (!current || !el || event.pointerId !== current.pointerId) return;
    drag.current = null;
    setDragging(false);
    const index = Math.max(0, Math.min(count - 1, Math.round(el.scrollLeft / TICK_WIDTH)));
    el.scrollTo({ left: index * TICK_WIDTH, behavior: 'smooth' });
  }

  function commitDraft() {
    if (draft === null) return;
    const next = snapTypedValue(draft, min, max, step);
    setDraft(null);
    if (next !== null && next !== clampedValue) onChange(next);
  }

  function handleScroll() {
    if (Date.now() < suppressUntil.current) return;
    const el = scrollRef.current;
    if (!el) return;

    if (liveFrame.current === undefined) {
      liveFrame.current = window.requestAnimationFrame(() => {
        liveFrame.current = undefined;
        const currentEl = scrollRef.current;
        if (!currentEl) return;
        const rawIndex = Math.max(0, Math.min(count - 1, Math.round(currentEl.scrollLeft / TICK_WIDTH)));
        setLiveValue(Math.round((min + rawIndex * step) * 100) / 100);
      });
    }

    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      const index = Math.round(el.scrollLeft / TICK_WIDTH);
      const clampedIndex = Math.max(0, Math.min(count - 1, index));
      const target = clampedIndex * TICK_WIDTH;
      if (Math.abs(el.scrollLeft - target) > 1) {
        el.scrollTo({ left: target, behavior: 'smooth' });
      }
      const nextValue = Math.round((min + clampedIndex * step) * 100) / 100;
      if (nextValue !== clampedValue) onChange(nextValue);
    }, 100);
  }

  const halfPad = Math.max(0, containerWidth / 2 - TICK_WIDTH / 2);

  return (
    <div className="wheel-picker">
      {draft === null ? (
        <button
          type="button"
          className="wheel-picker__value wheel-picker__value--editable"
          aria-label={`${ariaLabel}: ${label}${unitLabel ? ` ${unitLabel}` : ''}`}
          onClick={() => setDraft(String(clampedValue))}
        >
          {label}
          {unitLabel ? <span className="wheel-picker__unit"> {unitLabel}</span> : null}
        </button>
      ) : (
        <div className="wheel-picker__value">
          <input
            className="wheel-picker__input"
            type="text"
            inputMode="decimal"
            enterKeyHint="done"
            autoFocus
            aria-label={ariaLabel}
            value={draft}
            size={Math.max(3, draft.length + 1)}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitDraft}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commitDraft();
              if (event.key === 'Escape') setDraft(null);
            }}
          />
          {unitLabel ? <span className="wheel-picker__unit"> {unitLabel}</span> : null}
        </div>
      )}
      <div className="wheel-picker__frame">
        <div
          className={`wheel-picker__track${dragging ? ' wheel-picker__track--dragging' : ''}`}
          ref={scrollRef}
          onScroll={handleScroll}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          role="slider"
          aria-label={ariaLabel}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={clampedValue}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') onChange(Math.max(min, clampedValue - step));
            if (e.key === 'ArrowRight') onChange(Math.min(max, clampedValue + step));
          }}
        >
          <div className="wheel-picker__spacer" style={{ width: halfPad, flex: `0 0 ${halfPad}px` }} />
          {Array.from({ length: count }, (_, index) => {
            const tickValue = Math.round((min + index * step) * 100) / 100;
            const isMajor = index % majorEvery === 0;
            return (
              <div
                key={tickValue}
                className={`wheel-picker__tick${isMajor ? ' wheel-picker__tick--major' : ''}`}
                style={{ width: TICK_WIDTH, flex: `0 0 ${TICK_WIDTH}px` }}
              >
                <span className="wheel-picker__tick-line" />
                {isMajor ? <span className="wheel-picker__tick-label">{formatLabel ? formatLabel(tickValue) : tickValue}</span> : null}
              </div>
            );
          })}
          <div className="wheel-picker__spacer" style={{ width: halfPad, flex: `0 0 ${halfPad}px` }} />
        </div>
        <div className="wheel-picker__indicator" aria-hidden="true" />
      </div>
    </div>
  );
}
